import { SelectInputProps } from "@/types/components/SelectInputProps";
import { Icon } from "@iconify/react/dist/iconify.js";
import { ChangeEvent, Fragment, useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import useUuid from "@/Hooks/useUuid";
import { AuthorModel } from "@/types/models/AuthorModel";
import Row from "@/Components/Grid/Row";
import Column from "@/Components/Grid/Column";

const AuthorsRootField = ({ options, setData, dataKey = 'specialization_id', defaultValue = '', defaultLabel = '', className = '', label, storedData = [], errors }: {
    setData: (key: string, value: any) => void,
    dataKey?: string;
    options: SelectInputProps[],
    className?: string,
    label: string,
    defaultValue?: string,
    defaultLabel?: string,
    storedData?: AuthorModel[],
    errors: { [key: string]: string }
}) => {
    const [currentActive, setCurrentActive] = useState<string>(defaultValue);
    const [amountOfInputs, setAmountOfInputs] = useState<number>(1);
    const [selectedAuthors, setSelectedAuthors] = useState<any[]>([{id: useUuid(), value: ''}]);
    const [defaultAuthors, setDefaultAuthors] = useState<any[]>([]);
    const selectRefs = useRef<any[]>([]);

    useEffect(() => {
        if(storedData.length > 0){
            const storedAuthors: any[] = []
    
            storedData.map((a, i) => {
                storedAuthors.push({
                    id: useUuid(),
                    value: a.id
                })
            })
            
            setSelectedAuthors(storedAuthors)

            return () => {}
        }
    }, [])


    const handleReactSelectChange = (newValue: any, id: string) => {
        const updatedAuthors = selectedAuthors.map(author => {
            if (author.id === id) {
                return { ...author, value: newValue.value };
            }
            return author;
        });
        
        setSelectedAuthors(updatedAuthors);
        setData(dataKey, updatedAuthors);
    };

    const removeSelectedData = (id: string) => {
        const updatedAuthors = selectedAuthors.filter(author => author.id !== id);
        setSelectedAuthors(updatedAuthors);
        setData(dataKey, updatedAuthors);
    };

    const handleAddInput = () => {
        setSelectedAuthors(prevAuthors => [
            ...prevAuthors,
            { id: useUuid(), value: '' }
        ]);
    };

    const test = (index: number) => {
        selectRefs.current[index].blur()
    }


    return (
        <div className={`react-select-input-container relative`}>
            {selectedAuthors.map((author, i) => (
                <Fragment key={author.id}>
                    <Row className="items-center mb-6 relative">
                        <Column xl={10}>
                            <ReactSelect
                                ref={(el) => selectRefs.current[i] = el}
                                className={`react-select-input ${className}`}
                                onChange={(e) => handleReactSelectChange(e, author.id)}
                                options={options}
                                unstyled
                                defaultValue={{ 
                                    value: author.id,
                                    label: storedData.find(item => item.id === author.value)?.name_surname ? 
                                        storedData.find(item => item.id === author.value)?.name_surname + ' ' + storedData.find(item => item.id === author.value)?.degree + ' ' + storedData.find(item => item.id === author.value)?.affiliation : 'Dodaj autora' 
                                }}
                                placeholder={'Dodaj autora'}
                                noOptionsMessage={() => 'Nie znaleziono opcji'}
                                isClearable={false}
                                autoFocus={false}
                                onMenuOpen={() => test(i)}
                                key={author.id}
                                classNames={{
                                    control: (state) => {
                                        const borderColor = !state.isFocused ? 'border-royalPeriwinkle' : 'border-accordorange'

                                        return `transition-theme bg-arcticMist text-midnightNavy py-[15px] pl-4 pr-12 border-1 rounded ${borderColor} outline-none ring-0 ring-offset-0`
                                    },
                                    indicatorSeparator: (state) => {
                                        return 'hidden'
                                    },

                                    input: (state) => {
                                        return 'text-midnightNavy text-16 leading-6 tracking-[0.24px] border-0'
                                    },
                                    placeholder: (state) => {
                                        return 'text-midnightNavy text-16 leading-6 tracking-[0.24px]'
                                    },
                                    valueContainer: (state) => {
                                        return 'text-midnightNavy text-16 leading-6 tracking-[0.24px]'
                                    },
                                    menu: (state) => {
                                        return 'bg-arcticMist z-50 mt-2 shadow-selectShadow border-royalPeriwinkle rounded-[4px]'
                                    },
                                    dropdownIndicator: (state) => {
                                        const showClass = currentActive == '' ? 'cursor-pointer text-royalPeriwinkle w-[24px] h-[24px] absolute top-1/2 -translate-y-1/2 right-4 [&>svg]:w-[24px] [&>svg]:h-[24px]' : '!hidden'
                                        return showClass
                                    },
                                    clearIndicator: (state) => {
                                        return 'text-accordorange cursor-pointer w-[24px] h-[24px] absolute top-1/2 -translate-y-1/2 right-4 [&>svg]:w-[24px] [&>svg]:h-[24px]'
                                    },
                                    noOptionsMessage: (state) => {
                                        return 'bg-arcticMist mt-2 px-4 pt-1 pb-3 shadow-selectShadow border-royalPeriwinkle rounded-[4px] text-arcticMist text-16 leading-6'
                                    },
                                    option: (state) => {
                                        const bgColor = state.isFocused ? 'bg-accordorange text-midnightNavy' : ''
                                        return ` pl-4 py-3 pr-10 text-midnightNavy text-16 leading-6 ${bgColor}`
                                    },
                                }}
                            />
                        </Column>
                        <Column xl={2}>
                            <div className="flex gap-x-6 justify-center">
                                <button className="bg-crimsonAlert p-2 rounded-[4px] text-arcticMist transition-theme hover:opacity-75" type="button" onClick={() => removeSelectedData(author.id)}>
                                    <Icon icon={`ic:round-close`} width={24} />
                                </button>
                            </div>
                        </Column>
                    </Row>
                </Fragment>
            ))}
            <Row className="items-center relative">
                <Column xl={12}>
                    <div className="flex gap-x-2 justify-center">
                        <button className="bg-gradientButton p-1 rounded-[4px] text-arcticMist transition-theme hover:opacity-75" type="button" onClick={() => handleAddInput()}>
                            <Icon icon={`fluent:add-16-filled`} width={24} />
                        </button>
                        
                    </div>
                </Column>
            </Row>
        </div>
    )
}

export default AuthorsRootField;
